import { Link } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import { ScrollTrigger } from "gsap/ScrollTrigger";
import insuranceSquare from './../../assets/images/insurance-shapes-square.svg';
import insuranceTriangle from './../../assets/images/insurance-shapes-triangle.svg';
import '../insurance.scss';
import { gsap } from 'gsap';
import AOS from "aos";
import "aos/dist/aos.css";

export default function InsuranceUsage() {

  const square1Ref = useRef();
  const square2Ref = useRef();
  const triangle1Ref = useRef();
  const triangle2Ref = useRef();

  gsap.registerPlugin(ScrollTrigger);

  useEffect(() => {

    document.addEventListener('aos:in:bar-anim', function(ev) {
      //console.log('animated in', ev);

      if (ev.detail.querySelectorAll('.progress').length > 1) {
        //console.log("more than 1 progress");
        var barArray = [...ev.detail.querySelectorAll('.progress')];
      } else {
        //console.log("less than 1 progress");
        var barArray = [];
        var bar = ev.detail.querySelector('.progress');
        //console.log(bar);
        barArray.push(bar);
      }

      //console.log(barArray);

      if (barArray && barArray.length) {
        barArray.forEach(function(barItem) {
          var fill = barItem.getAttribute('data-fill');

          if (ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.height = fill + '%';
          }

          if (!ev.detail.classList.contains('vertical-bar-chart')) {
            barItem.style.width = fill + '%';
          }

        });
      }

      var Cont={val:0};

      if (ev.detail.querySelectorAll('.percentage').length > 1) {
        var percentageArray = [...ev.detail.querySelectorAll('.percentage')];
        //console.log("more than 1 percentage");
      } else {
        var percentageArray = [];
        var percentage = ev.detail.children[0].querySelector('.percentage');
        percentageArray.push(percentage);
        //console.log("less than percentage");

      }

      //console.log(percentage);

      if (percentageArray) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }

      //console.log(percentageArray);

    });

    document.addEventListener('aos:in:percentage', function(ev) {
      var Cont={val:0};
      var percentage = ev.detail.children[0].querySelector('.percentage');
      var percentageArray = [];
      percentageArray.push(percentage);
      //console.log(percentage);

      if (percentage) {
        percentageArray.forEach(function(percentage) {
          var percent = percentage.getAttribute('data-to');
            gsap.to(Cont,1,{val:percent,roundProps:"val",onUpdate:function(){
              percentage.textContent = Cont.val;
            },
            ease: "none"});
          
        });
      }
    });

    // Had to make some adjustments since it seemed like the animation wasn't playing nice with React. The shapes weren't incrementing exactly 90 degrees (it was adding a few degrees with decimal places) so they always rotated crooked. Possibly React strict mode causing issues. Added gsap.context, refs, and using GSAP's ScrollTrigger instead of AOS. 
    let ctx = gsap.context(() => {

      var tl = gsap.timeline({ 
        onComplete:rotateAgain,
        scrollTrigger: {
          trigger: ".shape-images"
        }
      });

      function animate(){
        tl
        .to(triangle1Ref.current, 0.5, {rotation:"+=90"})
        .to(square1Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(triangle2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
        .to(square2Ref.current, 0.5, {rotation:"+=90"}, "+=2")
      }

      function rotateAgain(){
        gsap.delayedCall(5, animate)
      }

      animate();

    });

    return () => ctx.revert(); // <- cleanup!

  }, []);

  useEffect(() => {
    AOS.init({
      once: true
    });
    AOS.refresh();
  }, []);

  return (
    <>
      <div className="insurance-usage">

        <p id="s1" className="intro" data-aos="fade-up">Explore proprietary stats and insights to see how today’s insurance businesses are using direct mail.&#8288;<a href="#reference-1" className="cm-article-content__reference" aria-label="Footnote 1" title="Footnote 1"><sup aria-hidden="true">[1]</sup></a></p>
        <div className='two-cols'>
          <div className='col-left'>
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Marketing Effectiveness</h3>

                <p className="pull-quote"><span data-aos className='highlighter'>87%</span> of insurance companies find direct mail <span data-aos className='highlighter'>effective.</span></p>
                <p>Insurance businesses say direct mail is more effective than any other channel.</p>
                <p>They also find direct mail more effective than companies in any other industry do.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Even transactional direct mail such as invoices and bills can be a powerful marketing tool, inspiring engagement and interaction. Personalizing mailpieces to speak to their unique needs, making it easy to connect to digital channels, and offering referral bonuses can all <strong><a href="/../marketing-with-first-class-mail-service/">give First&#8209;Class&nbsp;Mail<sup>&reg;</sup> extra mileage</a></strong>.</p>
              </div>
            </div>

            <div className='inner-cols'>
              <div className='inner-col-item'>
                <div className='personalized-box secondary align-to-text'>
                  <h3>Insurance companies often use mail to drive loyalty.</h3>
                  <div data-aos data-aos-id="vector-shapes" className='shape-images'>
                    <div className='triangle triangle-1'>
                      <img ref={triangle1Ref} src={insuranceTriangle} alt="" />
                    </div>
                    <div className='square square-1'>
                      <img ref={square1Ref} src={insuranceSquare} alt="" />
                    </div>
                    <div className='triangle triangle-2'>
                      <img ref={triangle2Ref} src={insuranceTriangle} alt="" />
                    </div>
                    <div className='square square-2'>
                      <img ref={square2Ref} src={insuranceSquare} alt="" />
                    </div>
                  </div>
                </div>
              </div>
              <div className='inner-col-item'>

                <div className='rounded-box-module-wrap'>
                  <div className="rounded-box-module" data-aos="fade-up">
                    <h3>Direct Mail Techniques</h3>
                    <div className='progress-bar' data-aos data-aos-id="bar-anim">
                      <div className='progress' data-fill="72"><span class="percentage" data-to="72" aria-hidden="true">0</span><span class="screen-reader-text">72</span>%</div>
                    </div>
                    <p>Personalized greetings or customer names</p>
                    <p>Among businesses in the insurance sector, personalization is the most popular direct mail technique. They’re also more likely than any other industry to use this technique.</p>
                  </div>
                  <div className="rounded-box-module">
                    <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                    <p>Personalized direct mail can help you maintain meaningful customer relationships. Do some segments show interest in certain insurance plans? Do you have a growing number of baby boomer borrowers? Assess how you can address these segments’ varying concerns and needs, and tailor messaging accordingly.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='col-right'>
            
            <div className='rounded-box-module-wrap'>
              <div className="rounded-box-module" data-aos="fade-up">
                <h3>Direct Mail and the Customer Journey</h3>

                <div className='progress-bar-area vertical-bar-chart' data-aos data-aos-id="bar-anim" data-aos-offset="300">
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="16"><span class="percentage" data-to="16" aria-hidden="true">0</span><span class="screen-reader-text">16</span>%</div>
                    </div>
                    <span className='label'>Drive awareness</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="25"><span class="percentage" data-to="25" aria-hidden="true">0</span><span class="screen-reader-text">25</span>%</div>
                    </div>
                    <span className='label'>Drive growth</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress fill-light' data-fill="25"><span class="percentage" data-to="25" aria-hidden="true">0</span><span class="screen-reader-text">25</span>%</div>
                    </div>
                    <span className='label'>Drive leads</span>
                  </div>
                  <div className='progressbar-item'>
                    <div className='progress-bar'>
                      <div className='progress' data-fill="34"><span class="percentage" data-to="34" aria-hidden="true">0</span><span class="screen-reader-text">34</span>%</div>
                    </div>
                    <span className='label'>Drive loyalty</span>
                  </div>
                </div>

                <p>In relation to the customer journey, insurance companies typically use direct mail at the end of the customer journey, to drive loyalty.</p>
              </div>
              <div className="rounded-box-module">
                <div className='heading-tag'><span className='heading-tag_span'>Pro tip</span></div>
                <p>Loyal customers are understood customers. Assess the data to get a grasp of their personal traits, preferences and behaviors. When and how often are they making purchases? How long have they been a customer? Do they frequent your social channels or stick to your website? These details will allow you to better anticipate customers’ needs and fine-tune your marketing efforts accordingly.</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
